import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Activity } from "lucide-react";
import PricingRequestModal from "@/components/PricingRequestModal";

const products = [
  {
    id: "strep-a",
    sku: "MRK250006",
    name: "Healgen Rapid Strep A Antigen Test",
    description: "CLIA-waived rapid immunoassay for the qualitative detection of Group A Streptococcal antigen from throat swab specimens. Results in just 5 minutes.",
    features: ["CLIA Waived", "5 min results", "25 tests/kit", "Throat swab"],
    badge: "CLIA Waived",
    regulatory: "FDA 510(k) Cleared",
    image: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/36hklcrv_healgen_strep_a.png"
  },
  {
    id: "covid-flu",
    sku: "MRK240016",
    name: "Healgen Rapid Check COVID-19 / Flu A & B Antigen Combo Test",
    description: "Combined differential detection of SARS-CoV-2, Influenza A, and Influenza B antigens from a single nasopharyngeal swab specimen. Streamlines respiratory season workflow.",
    features: ["3-in-1 Detection", "15 min results", "High accuracy", "NP swab"],
    badge: "EUA Authorized",
    regulatory: "FDA Emergency Use Authorization",
    image: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/bae9f7bt_healgen_covid_flu_pro.png"
  },
  {
    id: "rsv",
    sku: "MRK240002",
    name: "Healgen Rapid Check RSV Antigen Test",
    description: "Rapid qualitative detection of Respiratory Syncytial Virus (RSV) antigen. Essential for pediatric and geriatric care settings where RSV poses significant risk.",
    features: ["Rapid detection", "10 min results", "Room temp storage", "NP swab"],
    badge: "FDA Cleared",
    regulatory: "FDA 510(k) Cleared",
    image: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/n2n838ro_healgen_rsv.png"
  }
];

export default function ProductsPage() {
  const [pricingModalOpen, setPricingModalOpen] = useState(false);

  return (
    <div>
      {/* Pricing Request Modal */}
      <PricingRequestModal open={pricingModalOpen} onOpenChange={setPricingModalOpen} />

      {/* Header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
          <div className="max-w-3xl">
            <span className="caption-text text-sky-600 mb-4 block">Product Catalog</span>
            <h1 className="heading-primary mb-4" data-testid="products-page-headline">
              Point-of-Care Diagnostic Tests
            </h1>
            <p className="body-large" data-testid="products-page-description">
              Professional-grade rapid diagnostic tests designed for clinics, urgent care centers, 
              and healthcare facilities. All products meet FDA regulatory standards.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="bg-slate-50 section-gap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {products.map((product) => (
              <div 
                key={product.id} 
                className="card-product product-card group flex flex-col"
                data-testid={`product-listing-${product.id}`}
              >
                {/* Product Image */}
                <div className="aspect-[4/3] bg-slate-50 relative overflow-hidden">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="regulatory-badge">{product.badge}</span>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="mb-3">
                    <span className="sku-badge">SKU: {product.sku}</span>
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-sky-600 transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {product.name}
                  </h2>
                  <p className="body-default text-sm mb-4 flex-1">
                    {product.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.features.map((feature, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* Regulatory */}
                  <p className="text-xs text-slate-500 mb-4">
                    {product.regulatory}
                  </p>

                  {/* Actions */}
                  <div className="flex gap-3 mt-auto">
                    <Link to={`/products/${product.id}`} className="flex-1" data-testid={`view-details-${product.id}`}>
                      <Button variant="outline" className="w-full border-slate-300 hover:border-sky-600 hover:text-sky-600">
                        View Details
                      </Button>
                    </Link>
                    <Button 
                      onClick={() => setPricingModalOpen(true)}
                      className="bg-sky-600 hover:bg-sky-700 text-white"
                      data-testid={`request-pricing-${product.id}`}
                    >
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Use Notice */}
      <section className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="disclaimer-box">
            <div className="flex items-start gap-3">
              <Activity className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium mb-1">For Professional Use Only</p>
                <p className="text-sm text-amber-700">
                  All products listed are intended for professional in vitro diagnostic use only by trained 
                  healthcare personnel in clinical laboratory or point-of-care settings. Not available for 
                  consumer sale. For product specifications, pricing, and ordering information, please contact 
                  our sales team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Need Product Information or Pricing?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Our team is ready to assist with product specifications, pricing quotes, and ordering information.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => setPricingModalOpen(true)}
              className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-base"
              data-testid="products-request-pricing-btn"
            >
              Request Pricing
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Link to="/contact" data-testid="products-contact-sales-btn">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-base">
                Contact Sales Team
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
